import { Injectable } from '@angular/core';
import {
  ChartConfiguration,
  ChartDataset,
  ChartOptions,
  ElementOptionsByType,
  PluginOptionsByType,
  ScaleChartOptions,
  TooltipLabelStyle,
  TooltipOptions,
} from 'chart.js';
import { flattenDeep, min } from 'lodash-es';
import { DeepPartial } from 'node_modules/chart.js/dist/types/utils';

import { Utils } from '@shared/utils/utils';

import { RemainingSpendOverTimeConstants } from './remaining-spend-over-time.const';
import { TrialInsightsChartModel } from '../../models/trial-insights-chart.model';

export type TIRSChartLabel = number[][];
export type TIRSChartType = 'line';

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsRemainingSpendChartService
  implements TrialInsightsChartModel<TIRSChartType, TIRSChartLabel>
{
  minYAxes = 0;

  elementOptions = (): DeepPartial<ElementOptionsByType<TIRSChartType>> => {
    return {
      line: {
        tension: 0,
      },
      point: {
        radius: 0,
      },
    };
  };

  tooltipOptions = (): DeepPartial<TooltipOptions<TIRSChartType>> => {
    return {
      bodySpacing: 6,
      boxPadding: 6,
      callbacks: {
        label: (tooltipItem) => {
          if (!tooltipItem.raw) {
            return '';
          }

          return Utils.currencyFormatter(<number>tooltipItem.raw);
        },
        labelColor(tooltipItem): TooltipLabelStyle | void {
          return {
            backgroundColor: tooltipItem.dataset.borderColor as string,
            borderColor: tooltipItem.dataset.borderColor as string,
          };
        },
      },
    };
  };

  pluginOptions = (): DeepPartial<PluginOptionsByType<TIRSChartType>> => {
    return {
      legend: {
        display: false,
      },
      tooltip: this.tooltipOptions(),
    };
  };

  xAxesOptions = (): DeepPartial<ScaleChartOptions<TIRSChartType>> => {
    return {
      scales: {
        x1: {
          beginAtZero: true,
          grid: {
            display: false,
          },
          ticks: {
            display: true,
          },
          title: {
            display: false,
          },
        },
        x2: {
          display: false,
        },
        x3: {
          display: false,
        },
        x4: {
          display: false,
        },
        x5: {
          display: false,
        },
      },
    };
  };

  yAxesOptions = (): DeepPartial<ScaleChartOptions<TIRSChartType>> => {
    return {
      scales: {
        y: {
          beginAtZero: false,
          border: {
            display: false,
          },
          display: true,
          min: this.minYAxes,
          position: 'right',
          ticks: {
            callback: (value) => {
              if (value === 0 && this.minYAxes !== 0) {
                return;
              }

              return value ? Utils.currencyFormatter(value as number) : `${value}$`;
            },
            display: true,
          },
        },
      },
    };
  };

  chartOptions = (): ChartOptions<TIRSChartType> => {
    return {
      elements: this.elementOptions(),
      interaction: {
        intersect: false,
        mode: 'index',
      },
      maintainAspectRatio: false,
      plugins: this.pluginOptions(),
      responsive: true,
      scales: {
        ...this.xAxesOptions().scales,
        ...this.yAxesOptions().scales,
      },
    };
  };

  createChart = (
    datasets?: ChartDataset<TIRSChartType>[],
    labels?: string[]
  ): ChartConfiguration<TIRSChartType> => {
    return {
      data: {
        datasets: datasets || [],
        labels: labels || [],
      },
      options: this.chartOptions(),
      type: 'line',
    };
  };

  createDatasets = (remainingSpends: TIRSChartLabel): ChartDataset<TIRSChartType>[] => {
    const flattenSpends = flattenDeep(remainingSpends);

    this.minYAxes = min(flattenSpends) || 0;

    const chartLineColor = RemainingSpendOverTimeConstants.chartLineColor;

    return remainingSpends.map((spends, id) => {
      return <ChartDataset<TIRSChartType>>{
        backgroundColor: 'white',
        borderColor: chartLineColor[id],
        borderWidth: 5,
        data: spends,
        hoverBackgroundColor: chartLineColor[id],
        id,
        pointBackgroundColor: chartLineColor[id],
        pointBorderColor: chartLineColor[id],
        pointHoverBackgroundColor: chartLineColor[id],
        pointHoverBorderColor: chartLineColor[id],
        pointHoverRadius: 2,
        pointRadius: 2,
        pointStyle: 'circle',
        xAxisID: `x${id + 1}`,
      };
    });
  };
}
